import React from "react";
import NavBar from "component/NavBar";
import Footer from "component/Footer";
import Phone from "component/Phone";
import { anxiety1 } from "assets";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHeart,
  faCheckCircle,
  faLightbulb,
  faHandsHelping,
} from "@fortawesome/free-solid-svg-icons";

const anxietySymptoms = [
  "Persistent worry or fear",
  "Restlessness or feeling on edge",
  "Difficulty concentrating",
  "Physical symptoms (rapid heartbeat, sweating)",
  "Avoidance of situations",
  "Sleep disturbances",
];

const copingStrategies = [
  {
    icon: faShieldHeart,
    title: "Anxiety Assessment",
    description: "Understanding your triggers and response patterns",
  },
  {
    icon: faLightbulb,
    title: "Coping Mechanisms",
    description: "Evidence-based techniques to manage worry and stress",
  },
  {
    icon: faHandsHelping,
    title: "Ongoing Support",
    description: "Continuous guidance as you build resilience and confidence",
  },
];

const Anxiety = () => {
  return (
    <div className="bg-white">
      <NavBar />
      <Phone />

      <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
        <img
          className="w-full h-full object-cover"
          src={anxiety1}
          alt="Anxiety Treatment"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center animate-fade-in">
            <div className="inline-block px-4 py-2 bg-primary-500/90 rounded-full mb-4">
              <span className="text-white font-semibold text-body-sm">Mental Health Service</span>
            </div>
            <h1 className="text-display-sm md:text-display-md lg:text-display-lg text-white font-bold mb-4">
              Anxiety Treatment
            </h1>
            <p className="text-body-lg md:text-heading-sm text-white/90 max-w-2xl mx-auto">
              Professional support for managing worry and building resilience
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-6">
              You're Not Alone in Your Journey
            </h2>
            <div className="h-1 w-24 bg-primary-500 mx-auto mb-8"></div>
          </div>

          <div className="bg-white rounded-xl shadow-medium p-8 md:p-10">
            <p className="text-body-lg text-neutral-700 leading-relaxed mb-6">
              Are you grappling with persistent worries and fears? Be assured that we're here to provide comprehensive support throughout your journey. Our individualized treatment approach systematically explores the underlying causes of your anxiety, carefully analyzing your responses to triggers and their influence on your overall well-being.
            </p>
            <p className="text-body-lg text-neutral-700 leading-relaxed">
              Our objective is to equip you with adaptive coping mechanisms that foster a healthier mental state. It's important to understand that overcoming anxiety is a gradual process, not an instant fix. With our steadfast commitment and your proactive engagement, we can work together to effectively manage symptoms and navigate challenging thoughts with confidence.
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Common Anxiety Symptoms
            </h2>
            <p className="text-body-lg text-neutral-600">
              Understanding anxiety is the first step toward managing it
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {anxietySymptoms.map((symptom, index) => (
              <div
                key={index}
                className="bg-neutral-50 rounded-xl p-6 hover:bg-primary-50 transition-colors duration-300 group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-primary-500 text-xl group-hover:text-primary-600 transition-colors"
                    />
                  </div>
                  <p className="text-body-md text-neutral-800 font-medium">
                    {symptom}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              How We Help You Overcome Anxiety
            </h2>
            <p className="text-body-lg text-neutral-600 max-w-2xl mx-auto">
              A comprehensive approach to understanding and managing your anxiety
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {copingStrategies.map((strategy, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 p-8 text-center group"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary-500 transition-colors">
                  <FontAwesomeIcon
                    icon={strategy.icon}
                    className="text-2xl text-primary-600 group-hover:text-white transition-colors"
                  />
                </div>
                <h3 className="text-heading-sm text-neutral-900 mb-3">
                  {strategy.title}
                </h3>
                <p className="text-body-md text-neutral-600 leading-relaxed">
                  {strategy.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary-500 to-primary-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-heading-xl md:text-display-sm text-white mb-6">
            Ready to Find Peace of Mind?
          </h2>
          <p className="text-body-lg text-white/90 mb-8 leading-relaxed">
            Let us help you develop the tools and strategies to manage anxiety effectively and reclaim your life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-neutral-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                Get Started Today
              </button>
            </Link>
            <Link to="/mainservicepage">
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300">
                Explore Our Services
              </button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Anxiety;
